import { Component, TemplateRef, ViewChild, OnInit } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { HttpEventType } from "@angular/common/http";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  PlanPreparacionRespuestaService,
  PlanPreparacionRespuesta,
  PlanPreparacionRespuestaPayload,
} from "../../../../../shared/services/plan-preparacion-respuesta.service";
import { DigitalesService } from "../../../../../shared/services/digitales.service";
import { AdministracionService } from "../../../../../shared/services/administracion.services";
import { AuthService } from "../../../../../shared/services/auth.service";
import { EstadosDocumentoService } from "../../../../../shared/services/estados-documento.service";
import { gpcGoogleService } from "../../../../../shared/services/gpcGoogle.service";
import { GoogleStoragePreviewService } from "../../../../../shared/services/google-storage-preview.service";

function normalize(str: any) {
  return str
    ? str
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    : "";
}


@Component({
  selector: 'app-documentos',
  templateUrl: './documentos.component.html',
  styleUrls: ['./documentos.component.scss']
})
export class DocumentosPlanComponent implements OnInit {
  // Forms
  formCrear!: FormGroup;
  formEditar!: FormGroup;

  // Modales
  @ViewChild("modalCrear") modalCrear!: TemplateRef<any>;
  @ViewChild("modalVisualizar") modalVisualizar!: TemplateRef<any>;
  modalCrearRef!: NgbModalRef;
  modalEditarRef: any;

  // Datos
  historial: PlanPreparacionRespuesta[] = [];
  cargandoHistorial = false;
  estadosDocumento: any[] = [];
  usuarioLogin: any;
  empresaNit: string | null = null;

  // Tabla
  page1 = 1;
  pageSize1 = 10;
  searchText = "";

  // Edición
  editandoId: number | string | null = null;
  editando: any = null;
  codigoOriginal: string | null = null;
  cargandoEditar = false;

  // Archivo crear
  pdfFileSelected: File | null = null;

  // Archivo editar
  archivoSeleccionadoEditar: File | null = null;

  // Visualizar
  pdfUrl: SafeResourceUrl | null = null;
  descargando = false;

  hoy = "";

  // TODO: confirmar el id real del catálogo "digitales" para este documento
  private readonly idDigital = 7; // id de "digital" para plan de preparación y respuesta
  private readonly prefijoDigital = "PLAN_PREP_RESPUESTA";

  get tieneVigente(): boolean {
    return (this.historial || []).some((item) => this.esVigente(item));
  }

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private planPreparacionService: PlanPreparacionRespuestaService,
    private digitalesService: DigitalesService,
    private administracionesService: AdministracionService,
    private authService: AuthService,
    private estadoDocumentosService: EstadosDocumentoService,
    private gpcGoogle: gpcGoogleService,
    private googleStoragePreview: GoogleStoragePreviewService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.formCrear = this.fb.group({
      codigo: ["", Validators.required],
      version: ["1.0", Validators.required],
      fecha_designacion: ["", Validators.required],
      fecha_vencimiento: ["", Validators.required],
      estado: [1, Validators.required],
      motivo_creacion: ["", Validators.required],
      soporte: [null, Validators.required],
    });

    this.formEditar = this.fb.group({
      codigo: ["", Validators.required],
      fecha_designacion: ["", Validators.required],
      fecha_vencimiento: ["", Validators.required],
      estado: [1, Validators.required],
    });

    this.usuarioLogin = this.authService.getCurrentUserValue();
    this.hoy = new Date().toISOString().split("T")[0];

    this.cargarNitEmpresa();
    this.cargarEstadosDocumento();
    this.cargarHistorial();

    // Autocalcular vencimiento a 3 años desde la fecha de designación
    this.formCrear.get("fecha_designacion")?.valueChanges.subscribe((fecha) => {
      if (!fecha) return;
      const vencimiento = new Date(fecha);
      vencimiento.setFullYear(vencimiento.getFullYear() + 3);
      this.formCrear.patchValue({
        fecha_vencimiento: vencimiento.toISOString().split("T")[0],
      });
    });
  }

  // ===========================
  // Carga inicial
  // ===========================
  private cargarNitEmpresa(): void {
    this.administracionesService.obtenerTodas().subscribe({
      next: (res: any) => {
        const lista = Array.isArray(res) ? res : Array.isArray(res?.administraciones) ? res.administraciones : [];
        if (lista.length > 0) {
          this.empresaNit = lista[0].nit?.toString() || null;
        }
      },
      error: (err: any) => console.error("❌ Error al cargar NIT de la empresa:", err),
    });
  }

  private cargarEstadosDocumento(): void {
    this.estadoDocumentosService.getAllEstados().subscribe({
      next: (resp: any) => (this.estadosDocumento = resp),
      error: (error: any) => {
        console.error(error);
        Swal.fire("Error", "No fue posible cargar los estados del documento.", "error");
      },
    });
  }

  cargarHistorial(): void {
    this.cargandoHistorial = true;
    this.planPreparacionService.getAll().subscribe({
      next: (lista: any[]) => {
        this.historial = Array.isArray(lista) ? lista : [];
        this.cargandoHistorial = false;
      },
      error: (err) => {
        console.error("Error obteniendo planes de preparación y respuesta:", err);
        this.historial = [];
        this.cargandoHistorial = false;
      },
    });
  }

  // ===========================
  // Utilidades de estado / fecha
  // ===========================
  esActivo(item: any): boolean {
    return Number(item?.estado) === 3;
  }

  esVigente(item: any): boolean {
    if (!this.esActivo(item)) return false;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const inicio = item?.fecha_designacion ? new Date(item.fecha_designacion) : null;
    const fin = item?.fecha_vencimiento ? new Date(item.fecha_vencimiento) : null;

    if (inicio) {
      inicio.setHours(0, 0, 0, 0);
      if (inicio > hoy) return false;
    }
    if (fin) {
      fin.setHours(0, 0, 0, 0);
      if (fin < hoy) return false;
    }
    return true;
  }

  private obtenerEstadoTexto(item: any): string {
    const estadoId = Number(item?.estado);
    if ([1, 2].includes(estadoId)) return "BORRADOR";
    if (estadoId === 3) return "VIGENTE";
    if ([4, 5].includes(estadoId)) return "OBSOLETA";
    return "";
  }

  puedeEditar(item: any): boolean {
    return this.obtenerEstadoTexto(item) === "BORRADOR";
  }

  private mensajeNoEditable(item: any): string {
    switch (this.obtenerEstadoTexto(item)) {
      case "VIGENTE":
        return "El plan está vigente. Para modificarlo debe crear una nueva versión.";
      case "OBSOLETA":
        return "El plan está obsoleto. Solo se permite consulta.";
      default:
        return "Solo los planes en estado BORRADOR pueden editarse.";
    }
  }

  formatDateTodo(fecha: any): string {
    if (!fecha) return "N/A";
    const d = new Date(fecha);
    return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
  }

  private normalizarFechaParaInput(fecha: any): string {
    if (!fecha) return "";
    const valor = String(fecha);
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;
    const d = new Date(fecha);
    if (Number.isNaN(d.getTime())) return "";
    return this.formatearFechaParaInput(d);
  }

  private formatearFechaParaInput(fecha: Date): string {
    const año = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, "0");
    const dia = String(fecha.getDate()).padStart(2, "0");
    return `${año}-${mes}-${dia}`;
  }

  private normalizarCodigo(codigo: string): string {
    return String(codigo || "")
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }

  private obtenerMensajeError(err: any, porDefecto: string): string {
    const mensaje = err?.error?.message || err?.error?.error || err?.message || porDefecto;
    if (typeof mensaje !== "string") return porDefecto;
    if (mensaje.toLowerCase().startsWith("http failure response for")) return porDefecto;
    return mensaje;
  }

  private mostrarSwal(icon: "success" | "error" | "warning" | "info" | "question", titulo: string, texto?: string): void {
    Swal.fire({ icon, title: titulo, text: texto, confirmButtonColor: "#0d6efd" });
  }

  // ===========================
  // Listado / filtro / paginación
  // ===========================
  get historialVista(): any[] {
    const base = this.historial || [];
    const search = normalize(this.searchText);
    if (!search) return base;

    return base.filter((item: any) => {
      const codigo = normalize(item?.codigo || "");
      const version = normalize(item?.version || "");
      const fechaDesignacion = normalize(this.formatDateTodo(item?.fecha_designacion));
      const fechaVencimiento = normalize(this.formatDateTodo(item?.fecha_vencimiento));
      const estado = this.esActivo(item) ? "activo" : "inactivo";
      return (
        codigo.includes(search) ||
        version.includes(search) ||
        fechaDesignacion.includes(search) ||
        fechaVencimiento.includes(search) ||
        estado.includes(search)
      );
    });
  }

  aplicarFiltro(): void {
    this.page1 = 1;
  }

  // ===========================
  // Exportar
  // ===========================
  exportarExcel(): void {
    const datos = this.historialVista || [];
    if (!datos.length) {
      this.mostrarSwal("info", "Sin datos", "No hay planes de preparación y respuesta para exportar");
      return;
    }

    const filas = datos.map((item: any) => ({
      CODIGO: item?.codigo || "",
      VERSION: item?.version || "",
      DESIGNACION: this.formatDateTodo(item?.fecha_designacion),
      VENCIMIENTO: this.formatDateTodo(item?.fecha_vencimiento),
      ESTADO: this.esActivo(item) ? "ACTIVO" : "INACTIVO",
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filas);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plan Preparacion Respuesta");
    XLSX.writeFile(wb, `plan_preparacion_respuesta_${Date.now()}.xlsx`);
    this.mostrarSwal("success", "Éxito", "Exportación a Excel completada");
  }

  exportarPdf(): void {
    const datos = this.historialVista || [];
    if (!datos.length) {
      this.mostrarSwal("info", "Sin datos", "No hay planes de preparación y respuesta para exportar");
      return;
    }

    const doc = new jsPDF("p", "pt");
    doc.setFontSize(12);
    doc.text("Historial de Plan de Preparación y Respuesta ante Emergencias", 40, 35);

    autoTable(doc, {
      head: [["CODIGO", "VERSION", "DESIGNACION", "VENCIMIENTO", "ESTADO"]],
      body: datos.map((item: any) => [
        item?.codigo || "",
        item?.version || "",
        this.formatDateTodo(item?.fecha_designacion),
        this.formatDateTodo(item?.fecha_vencimiento),
        this.esActivo(item) ? "ACTIVO" : "INACTIVO",
      ]),
      startY: 50,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [13, 110, 253] },
    });

    doc.save(`plan_preparacion_respuesta_${Date.now()}.pdf`);
    this.mostrarSwal("success", "Éxito", "Exportación a PDF completada");
  }

  // ===========================
  // Crear
  // ===========================
  abrirModalCrear(): void {
    if (this.tieneVigente) {
      Swal.fire({
        icon: "warning",
        title: "No se puede crear",
        text: "Ya existe un plan de preparación y respuesta vigente. Inactive el actual o espere a que venza para crear uno nuevo.",
      });
      return;
    }

    this.modalCrearRef = this.modalService.open(this.modalCrear, { centered: true, size: "lg" });
    this.modalCrearRef.result.finally(() => {
      this.formCrear.reset({ version: "1.0", estado: 1, motivo_creacion: "" });
      this.pdfFileSelected = null;
    });
  }

  onPdfFileSelected(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    this.asignarPdf(file);
    event.target.value = "";
  }

  onDragOverPdf(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.add("drag-over");
  }

  onDragLeavePdf(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
  }

  onDropPdf(event: DragEvent): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.remove("drag-over");
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.asignarPdf(file);
    event.dataTransfer?.clearData();
  }

  private asignarPdf(file: File): void {
    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (extension !== "pdf") {
      Swal.fire("Error", "Solo se permiten archivos PDF", "error");
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      Swal.fire("Error", "El archivo no debe superar los 10MB", "error");
      return;
    }

    this.pdfFileSelected = file;
    this.formCrear.patchValue({ soporte: file });
    this.formCrear.get("soporte")?.updateValueAndValidity();
  }

  removePdfSeleccionado(): void {
    this.pdfFileSelected = null;
    this.formCrear.patchValue({ soporte: null });
    this.formCrear.get("soporte")?.updateValueAndValidity();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  guardar(): void {
    if (this.tieneVigente) {
      Swal.fire({
        icon: "warning",
        title: "No se puede guardar",
        text: "Ya existe un plan de preparación y respuesta vigente. Inactive el actual o espere a que venza para crear uno nuevo.",
      });
      return;
    }

    if (this.formCrear.invalid) {
      this.formCrear.markAllAsTouched();
      Swal.fire({ icon: "warning", title: "Campos requeridos", text: "Debe completar toda la información." });
      return;
    }

    const valores = this.formCrear.value;
    const { soporte, ...payload } = valores as PlanPreparacionRespuestaPayload & { soporte: File };

    Swal.fire({
      title: "Guardando plan de preparación y respuesta...",
      text: "Espere un momento",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    this.planPreparacionService.create(payload).subscribe({
      next: (resp: any) => {
        Swal.close();
        const idCreado = resp?.id ?? resp?.data?.id ?? null;
        const exitoExplicito = resp?.success === true || resp?.ok === true;
        const ok = !!idCreado || exitoExplicito;

        if (!ok) {
          Swal.fire({ icon: "warning", title: "Atención", text: resp?.message || "Respuesta no exitosa" });
          return;
        }

        if (soporte) {
          this.subirArchivo(
            soporte,
            idCreado,
            `${this.empresaNit}/pesv/documental/plan-preparacion-respuesta/${valores.codigo}`,
            `PLAN_PREP_RESPUESTA_${valores.codigo}_PESV.pdf`,
            this.prefijoDigital,
            "Plan de preparación y respuesta y documento guardados correctamente",
          );
        } else {
          this.mostrarSwal("success", "Éxito", resp?.message || "Plan de preparación y respuesta guardado correctamente");
        }

        this.formCrear.reset({ version: "1.0", estado: 1, motivo_creacion: "" });
        this.pdfFileSelected = null;
        this.modalCrearRef?.close();
        this.cargarHistorial();
      },
      error: (error) => {
        const mensaje = this.obtenerMensajeError(error, "Error inesperado");
        Swal.fire({ icon: "error", title: "No se pudo guardar", text: mensaje });
      },
    });
  }

  private subirArchivo(
    archivo: File,
    idDigital: number,
    carpeta: string,
    nombreArchivo: string,
    prefijo: string,
    mensajeExito: string,
  ): void {
    this.digitalesService.subirArchivo(archivo, idDigital, carpeta, nombreArchivo, prefijo).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.mostrarSwal("success", "Éxito", mensajeExito);
          this.cargarHistorial();
        }
      },
      error: (err: any) => {
        console.error("❌ Error al subir:", err);
        this.mostrarSwal("error", "Error", this.obtenerMensajeError(err, "Error al subir el documento"));
      },
    });
  }

  // ===========================
  // Editar
  // ===========================
  editarDesdeHistorial(item: any): void {
    if (!item || item.id == null) {
      this.mostrarSwal("error", "Error", "No se encontró el identificador del plan a editar");
      return;
    }

    if (!this.puedeEditar(item)) {
      Swal.fire({ icon: "warning", title: "No se puede editar", text: this.mensajeNoEditable(item) });
      return;
    }

    this.editando = item;
    this.editandoId = item.id;
    this.codigoOriginal = this.normalizarCodigo(item.codigo || "");
    this.archivoSeleccionadoEditar = null;

    this.formEditar.patchValue({
      codigo: item.codigo || "",
      fecha_designacion: this.normalizarFechaParaInput(item.fecha_designacion),
      fecha_vencimiento: this.normalizarFechaParaInput(item.fecha_vencimiento),
      estado: item.estado ?? 1,
    });
  }

  abrirModalEditar(template: any): void {
    this.modalEditarRef = this.modalService.open(template, { centered: true, size: "lg" });
    this.modalEditarRef.result.finally(() => {
      this.archivoSeleccionadoEditar = null;
      this.formEditar.reset();
      this.editandoId = null;
      this.editando = null;
      this.codigoOriginal = null;
    });
  }

  onFileChangeEditar(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "pdf") {
      Swal.fire("Validación", "Solo se permite reemplazar con archivo PDF", "warning");
      event.target.value = "";
      this.archivoSeleccionadoEditar = null;
      return;
    }

    this.archivoSeleccionadoEditar = file;
  }

  eliminarArchivoEditar(): void {
    this.archivoSeleccionadoEditar = null;
  }

  guardarCambios(modal: any): void {
    if (!this.formEditar.valid) {
      this.formEditar.markAllAsTouched();
      this.mostrarSwal("warning", "Validación", "Completa todos los campos requeridos");
      return;
    }

    if (!this.editandoId) {
      this.mostrarSwal("error", "Error", "No se encontró el identificador del plan");
      return;
    }

    const codigo = this.normalizarCodigo(this.formEditar.get("codigo")?.value);
    const fechaDesignacion = String(this.formEditar.get("fecha_designacion")?.value || "").trim();
    const fechaVencimiento = String(this.formEditar.get("fecha_vencimiento")?.value || "").trim();
    const codigoOriginal = this.codigoOriginal || "";
    const sinCambioDeVersion = !!codigoOriginal && codigo === codigoOriginal;

    if (!codigo || !fechaDesignacion || !fechaVencimiento) {
      this.mostrarSwal("error", "Error", "Código, fecha de designación y fecha de vencimiento son obligatorios");
      return;
    }

    const hoy = this.formatearFechaParaInput(new Date());
    if (fechaDesignacion > hoy) {
      this.mostrarSwal("warning", "Fecha inválida", "La fecha de designación no puede ser futura en edición");
      return;
    }

    this.planPreparacionService.getAll().subscribe({
      next: (lista: any[]) => {
        const existente = (Array.isArray(lista) ? lista : []).find(
          (item: any) => this.normalizarCodigo(item?.codigo || "") === codigo,
        );
        const duplicado = !!existente && String(existente?.id) !== String(this.editandoId);

        if (duplicado) {
          this.mostrarSwal("warning", "Código duplicado", `El código ${codigo} ya existe. Cambia el código para poder guardar.`);
          return;
        }

        this.cargandoEditar = true;

        const payload = sinCambioDeVersion
          ? {
              fecha_designacion: fechaDesignacion,
              fecha_vencimiento: fechaVencimiento,
              estado: this.formEditar.get("estado")?.value,
            }
          : {
              codigo,
              fecha_designacion: fechaDesignacion,
              fecha_vencimiento: fechaVencimiento,
              estado: this.formEditar.get("estado")?.value,
            };

        const archivoEditar = this.archivoSeleccionadoEditar;

        this.planPreparacionService.update(this.editandoId as number, payload).subscribe({
          next: () => {
            if (!archivoEditar) {
              this.finalizarEdicion(modal, "Plan de preparación y respuesta actualizado correctamente");
              return;
            }

            const nombreArchivo = `PLAN_PREP_RESPUESTA_${codigo}_PESV.pdf`;
            const carpeta = `${this.empresaNit}/pesv/documental/plan-preparacion-respuesta/${codigo}`;

            this.digitalesService.subirArchivo(archivoEditar, this.idDigital, carpeta, nombreArchivo, this.prefijoDigital).subscribe({
              next: (event: any) => {
                if (event.type === HttpEventType.Response) {
                  this.finalizarEdicion(modal, "Plan y documento actualizados correctamente");
                }
              },
              error: () => {
                this.cargandoEditar = false;
                this.mostrarSwal("error", "Error", "Se actualizó el registro, pero no se pudo reemplazar el documento.");
                this.cargarHistorial();
              },
            });
          },
          error: (err) => {
            this.cargandoEditar = false;
            this.mostrarSwal("error", "Error", this.obtenerMensajeError(err, "No se pudo actualizar el plan"));
          },
        });
      },
      error: () => this.mostrarSwal("error", "Error", "No se pudo verificar el plan"),
    });
  }

  private finalizarEdicion(modal: any, mensaje: string): void {
    this.cargandoEditar = false;
    this.mostrarSwal("success", "Éxito", mensaje);
    this.editandoId = null;
    this.editando = null;
    this.codigoOriginal = null;
    this.archivoSeleccionadoEditar = null;
    this.formEditar.reset();
    this.cargarHistorial();
    modal.dismiss();
  }

  // ===========================
  // Cambiar estado
  // ===========================
  cambiarEstado(item: any, activar: boolean): void {
    const id = item?.id;
    if (!id) {
      this.mostrarSwal("error", "Error", "No se encontró el identificador del plan");
      return;
    }

    const yaEnEstado = activar ? this.esActivo(item) : !this.esActivo(item);
    if (yaEnEstado) {
      this.mostrarSwal("info", "Sin cambios", activar ? "El plan ya está activo" : "El plan ya está inactivo");
      return;
    }

    Swal.fire({
      title: "Confirmar acción",
      text: activar ? "¿Deseas activar este plan?" : "¿Deseas inactivar este plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí",
      cancelButtonText: "No",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const request$ = activar ? this.planPreparacionService.activar(id) : this.planPreparacionService.inactivar(id);
      request$.subscribe({
        next: () => {
          this.mostrarSwal("success", "Éxito", activar ? "Plan activado correctamente" : "Plan inactivado correctamente");
          this.cargarHistorial();
        },
        error: (err) => {
          this.mostrarSwal(
            "error",
            "Error",
            this.obtenerMensajeError(err, activar ? "No se pudo activar el plan" : "No se pudo inactivar el plan"),
          );
        },
      });
    });
  }

  // ===========================
  // Visualizar / descargar
  // ===========================
  visualizarPDF(item: any): void {
    if (!item.codigo) {
      Swal.fire("Error", "No se puede visualizar sin código", "error");
      return;
    }

    Swal.fire({
      title: "Confirmar visualización",
      text: "¿Deseas visualizar el documento: Plan de Preparación y Respuesta?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Visualizar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      if (!this.empresaNit) {
        Swal.close();
        Swal.fire("Error", "No se pudo obtener el NIT de la empresa para visualizar el documento.", "error");
        return;
      }

      const nombreArchivo = `PLAN_PREP_RESPUESTA_documental.pdf`;
      const ruta = `${this.empresaNit}/pesv/documental/plan-preparacion-respuesta/${item.codigo}/${item.id}/${nombreArchivo}`;

      this.gpcGoogle.getSignedUrlFiles(ruta).subscribe({
        next: (resp) => {
          Swal.close();
          if (!resp?.url) {
            Swal.fire("Error", "No se pudo obtener la URL del documento.", "error");
            return;
          }
          this.googleStoragePreview.open(resp.url, "Plan de Preparación y Respuesta PESV");
        },
        error: (err) => {
          Swal.close();
          console.error("❌ Error al obtener la URL del documento:", err);
          Swal.fire("Error", "No se pudo obtener la URL del documento.", "error");
        },
      });
    });
  }

  descargarDocumento(item: any): void {
    if (!item.codigo) {
      Swal.fire("Error", "No se puede descargar sin código", "error");
      return;
    }

    if (!this.empresaNit) {
      Swal.fire("Error", "No se pudo obtener el NIT de la empresa para descargar el documento.", "error");
      return;
    }

    Swal.fire({
      title: "Descargando...",
      text: "Por favor espera mientras se descarga el documento.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    const nombreArchivo = `PLAN_PREP_RESPUESTA_documental.pdf`;
    const ruta = `${this.empresaNit}/pesv/documental/plan-preparacion-respuesta/${item.codigo}/${item.id}/${nombreArchivo}`;

    this.gpcGoogle.getSignedUrlFiles(ruta).subscribe({
      next: (resp) => {
        Swal.close();
        if (!resp?.url) {
          Swal.fire("Error", "No se pudo obtener la URL firmada del documento.", "error");
          return;
        }
        this.descargarDesdeUrlFirmada(resp.url, `PLAN_PREP_RESPUESTA_${item.id}.pdf`);
      },
      error: (err) => {
        Swal.close();
        console.error("❌ Error al obtener la URL firmada del documento:", err);
        Swal.fire("Error", "No se pudo obtener la URL firmada del documento.", "error");
      },
    });
  }

  private descargarDesdeUrlFirmada(url: string, fileName: string): void {
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Error al descargar archivo: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const enlace = document.createElement("a");
        enlace.href = objectUrl;
        enlace.download = fileName;
        enlace.style.display = "none";
        document.body.appendChild(enlace);
        enlace.click();
        document.body.removeChild(enlace);
        window.URL.revokeObjectURL(objectUrl);
      })
      .catch((error) => {
        console.error("Error al descargar URL firmada:", error);
        Swal.fire("Error", "No se pudo descargar el documento.", "error");
      });
  }
}